import { Alert, Button, ConfigProvider, Modal, Popconfirm, Table } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { WaterMeterRepo } from "@/core/repos/tables/water-meter.repo";
import {
    WaterMeterChangeModel,
} from "@/core/models/water-meter-change.model";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { formatUtcDateTimePipe } from "@/core/pipes/format-date.pipe";
import {
    WaterMeterChangeStatus,
    getWaterMeterChangeReasonDisplay,
} from "@/core/enums/global.enums";
import { waterMeterChangeStatusTagPipe } from "@/core/pipes/water-meter-change-status.pipe";

const waterMeterRepo = container.get(WaterMeterRepo);

interface ChangeMeterHistoryModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    data: ContractModel;
}

export const ChangeMeterHistoryModal: React.FC<ChangeMeterHistoryModalProps> = ({
    isModalOpen,
    setIsModalOpen,
    data,
}) => {
    const permissionStore = usePermissionStore();
    const [loading, setLoading] = useState<boolean>(false);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const [records, setRecords] = useState<WaterMeterChangeModel[]>([]);

    const fetchChanges = async () => {
        if (!data.id) {
            setRecords([]);
            return;
        }
        setLoading(true);
        try {
            const result = await waterMeterRepo.getChanges(data.id);
            setRecords(result);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            fetchChanges();
        }
    }, [isModalOpen, data.id]);

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleCancelChange = async (record: WaterMeterChangeModel) => {
        setCancellingId(record.id);
        try {
            const response = await waterMeterRepo.cancelChange(record.id);
            if (response?.code === "00") {
                Modal.success({ content: "Đã huỷ thay đổi đồng hồ." });
                await fetchChanges();
            } else {
                Modal.error({
                    content: response?.errorMessage ?? "Huỷ thay đổi thất bại.",
                });
            }
        } finally {
            setCancellingId(null);
        }
    };

    const canCancel = permissionStore.page.edit;

    const columns = [
        {
            title: "Ngày thay",
            dataIndex: "changeDate",
            key: "changeDate",
            render: (value: string | null) => (value ? formatUtcDateTimePipe(value) : ""),
        },
        {
            title: "Kỳ",
            key: "period",
            render: (_: any, record: WaterMeterChangeModel) => `${record.month}/${record.year}`,
        },
        {
            title: "ĐH cũ",
            dataIndex: "oldWaterMeterCode",
            key: "oldWaterMeterCode",
        },
        {
            title: "Chỉ số tháo",
            dataIndex: "removedIndex",
            key: "removedIndex",
        },
        {
            title: "ĐH mới",
            dataIndex: "newWaterMeterCode",
            key: "newWaterMeterCode",
        },
        {
            title: "Chỉ số đầu",
            dataIndex: "headIndex",
            key: "headIndex",
        },
        {
            title: "Lý do",
            dataIndex: "reason",
            key: "reason",
            render: (value: string) => getWaterMeterChangeReasonDisplay(value) || value,
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            key: "status",
            render: (value: string) => waterMeterChangeStatusTagPipe(value),
        },
        {
            title: "Thao tác",
            key: "action",
            render: (_: any, record: WaterMeterChangeModel) =>
                record.status === WaterMeterChangeStatus.Confirmed && canCancel ? (
                    <Popconfirm
                        title="Huỷ thay đổi đồng hồ?"
                        description="Huỷ KHÔNG hoàn lại trạng thái đồng hồ cũ (đồng hồ mới vẫn Active). Chỉ xoá snapshot chỉ số của kỳ đó. Tiếp tục?"
                        okText="Huỷ thay đổi"
                        cancelText="Đóng"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleCancelChange(record)}
                    >
                        <Button danger loading={cancellingId === record.id}>
                            Huỷ
                        </Button>
                    </Popconfirm>
                ) : null,
        },
    ];

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                width={1000}
                style={{ top: 20 }}
                styles={{
                    mask: {
                        backdropFilter: "blur(2px)",
                        backgroundColor: "var(--primary-mask)",
                    },
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <Button onClick={handleCancel}>Đóng</Button>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">
                        LỊCH SỬ THAY ĐỒNG HỒ - {data.code} - {data.name}
                    </p>
                </div>

                <Alert
                    type="warning"
                    showIcon
                    message="Lưu ý khi huỷ"
                    description="Nút Huỷ chỉ xoá snapshot chỉ số của kỳ đó, KHÔNG hoàn lại trạng thái đồng hồ (đồng hồ mới vẫn đang Active). Ưu tiên kiểm tra kỹ trước khi thay."
                    style={{ margin: "12px 8px" }}
                />

                <Table<WaterMeterChangeModel>
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={records}
                    pagination={false}
                    size="middle"
                    scroll={{ x: "max-content" }}
                    locale={{ emptyText: "Chưa có lịch sử thay đồng hồ" }}
                />
            </Modal>
        </ConfigProvider>
    );
};
